import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchPlayoutStatus } from '../api';
import { PlayoutServerStatusEntry, PlayoutServerStatusMap, YouTubeLiveStream } from '../types';

const defaultStatus: PlayoutServerStatusEntry = {
  ffmpeg: { isRunning: false, startTime: null, uptime: 0, pid: null },
  state: 'idle',
  reachable: undefined,
  error: null
};

export function usePlayoutStatuses(streams: YouTubeLiveStream[]) {
  const [statusMap, setStatusMap] = useState<PlayoutServerStatusMap>({});
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const statusRef = useRef<PlayoutServerStatusMap>({});
  const streamsRef = useRef<YouTubeLiveStream[]>(streams);

  useEffect(() => {
    statusRef.current = statusMap;
  }, [statusMap]);

  useEffect(() => {
    streamsRef.current = streams;
  }, [streams]);

  const refreshStatuses = useCallback(
    async (streamsList?: YouTubeLiveStream[], skipUnreachable = false, specificServerId?: string) => {
      const activeStreams = streamsList ?? streamsRef.current;

      // Build map of playout servers to poll.
      // - On initial/manual load (skipUnreachable=false): check ALL streams with a playout server
      // - On periodic poll (skipUnreachable=true): only check live streams to avoid noise from offline servers
      // - specificServerId: always include (manual refresh button)
      const liveOnly = skipUnreachable;
      const playoutServerMap = new Map<string, string>();
      activeStreams.forEach(stream => {
        if (!stream.playout_server_id || playoutServerMap.has(stream.playout_server_id)) return;
        const isTargeted = specificServerId === stream.playout_server_id;
        if (isTargeted || !liveOnly || stream.status === 'live') {
          playoutServerMap.set(stream.playout_server_id, stream.id);
        }
      });

      if (playoutServerMap.size === 0) return;

      // Mark servers we're about to poll as loading
      setStatusMap(prev => {
        const next = { ...prev };
        playoutServerMap.forEach((_, serverId) => {
          if (!skipUnreachable || prev[serverId]?.reachable !== false) {
            next[serverId] = {
              ...defaultStatus,
              ...prev[serverId],
              reachable: undefined,
              error: null
            };
          }
        });
        return next;
      });

      const statusUpdates: PlayoutServerStatusMap = {};

      await Promise.all(
        Array.from(playoutServerMap.entries()).map(async ([serverId, streamId]) => {
          if (skipUnreachable && statusRef.current[serverId]?.reachable === false) {
            return;
          }

          try {
            const playoutStatus = await fetchPlayoutStatus(streamId);
            const isReachable = playoutStatus.reachable !== false;
            statusUpdates[serverId] = {
              ffmpeg: playoutStatus.ffmpeg || defaultStatus.ffmpeg,
              state: (playoutStatus.state as PlayoutServerStatusEntry['state']) || 'idle',
              reachable: isReachable,
              error: isReachable ? null : playoutStatus.error || 'Server unreachable',
              downloadQueue: playoutStatus.downloadQueue,
              cleanupLogs: playoutStatus.cleanupLogs
            };
          } catch (error: any) {
            statusUpdates[serverId] = {
              ...defaultStatus,
              state: 'idle',
              reachable: false,
              error: error?.response?.data?.error || error?.message || 'Server unreachable'
            };
          }
        })
      );

      if (Object.keys(statusUpdates).length > 0) {
        setStatusMap(prev => ({ ...prev, ...statusUpdates }));
      }
    },
    []
  );

  useEffect(() => {
    if (streamsRef.current.length === 0) return;

    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
    }

    // Periodic poll: only live streams (skipUnreachable=true).
    // Skip while the tab is hidden so background tabs don't keep polling.
    pollIntervalRef.current = setInterval(() => {
      if (document.hidden) return;
      refreshStatuses(undefined, true);
    }, 300000);

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [streams.length, refreshStatuses]);

  return {
    statusMap,
    refreshStatuses
  };
}
